import { useState } from 'react';
import { Loader2, CheckCircle2, Copy, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { onboardingApi } from '@/lib/api';

interface Props {
  tenantId:   string;
  token:      string;
  tenantSlug: string;
  plan:       string;
  adminEmail: string;
  adminName:  string;
  adminPassword: string;
  onBack: () => void;
}

const PLAN_LABELS: Record<string, string> = {
  base: 'Base — ₹1,999/mo',
  plus: 'Plus — ₹3,999/mo',
  pro:  'Pro — ₹6,999/mo',
};

const CHECKLIST = [
  'Add doctors and staff from Users → Add User',
  'Configure OPD slots from Settings → OPD Slots',
  'Set up billing heads from Settings → Billing',
  'Register your first patient and open the OPD queue',
];

export default function Step5GoLive({
  tenantId, token, tenantSlug, plan,
  adminEmail, adminName, adminPassword, onBack,
}: Props) {
  const [provisioning, setProvisioning] = useState(false);
  const [done, setDone]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function provision() {
    setError(null);
    setProvisioning(true);
    try {
      await onboardingApi.provision(tenantId, token, {
        plan, adminName, adminEmail, adminPassword,
      });
      localStorage.removeItem('as_ob_session');
      setDone(true);
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Provisioning failed. Please contact support.');
    } finally {
      setProvisioning(false);
    }
  }

  function copyCredentials() {
    const text = `Hospital ID: ${tenantSlug}\nEmail: ${adminEmail}\nPassword: ${adminPassword}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (done) {
    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-clinical-success/10 flex items-center justify-center">
            <CheckCircle2 size={36} className="text-clinical-success"/>
          </div>
        </div>
        <div>
          <h3 className="text-xl font-bold text-navy">Hospital provisioned successfully</h3>
          <p className="text-gray-400 text-sm mt-1">The hospital account is live. Share these credentials with the Hospital Admin.</p>
        </div>

        {/* Credentials box */}
        <div className="bg-navy/5 rounded-xl p-4 text-left space-y-2 font-mono text-sm">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs font-sans font-semibold text-navy uppercase tracking-wide">Hospital Admin Credentials</span>
            <button onClick={copyCredentials}
              className="flex items-center gap-1 text-xs text-orange hover:text-orange-dark">
              <Copy size={12}/>{copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Hospital ID</span>
            <span className="text-navy font-semibold">{tenantSlug}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Email</span>
            <span className="text-navy">{adminEmail}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Password</span>
            <span className="text-navy">{adminPassword}</span>
          </div>
        </div>

        {/* Onboarding checklist */}
        <div className="text-left">
          <p className="text-xs font-semibold text-navy uppercase tracking-wide mb-3">Getting started checklist</p>
          <ul className="space-y-2">
            {CHECKLIST.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                <span className="w-5 h-5 rounded-full border-2 border-gray-200 flex items-center justify-center flex-shrink-0 text-2xs text-gray-400 font-bold mt-0.5">
                  {i + 1}
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <Link to="/admin/hospitals"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-orange
                     text-navy font-semibold text-sm hover:bg-orange-dark transition-colors">
          Back to Hospitals <ArrowRight size={16}/>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {[
          ['Hospital ID', tenantSlug],
          ['Admin Email', adminEmail],
          ['Plan', PLAN_LABELS[plan] ?? plan],
        ].map(([label, value]) => (
          <div key={label} className="flex justify-between items-center px-4 py-3">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-sm font-medium text-navy font-mono">{value}</span>
          </div>
        ))}
      </div>

      <div className="bg-orange/5 border border-orange/20 rounded-xl px-4 py-3 text-sm text-navy/70">
        Clicking <strong className="text-navy">Provision & Go Live</strong> will create the hospital's
        database schema, set up module permissions per the selected plan, and activate the account.
        This takes about 10–15 seconds.
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button type="button" onClick={onBack} disabled={provisioning}
          className="flex-1 py-3 rounded-lg border border-gray-200 text-navy text-sm font-medium
                     hover:bg-gray-50 transition-colors disabled:opacity-40">
          Back
        </button>
        <button type="button" onClick={provision} disabled={provisioning}
          className="flex-1 py-3 rounded-lg bg-orange text-navy font-semibold text-sm
                     hover:bg-orange-dark transition-colors disabled:opacity-50
                     flex items-center justify-center gap-2">
          {provisioning ? (
            <><Loader2 size={16} className="animate-spin"/> Provisioning…</>
          ) : (
            <>Provision & Go Live <ArrowRight size={16}/></>
          )}
        </button>
      </div>
    </div>
  );
}
